import type { AirMetar } from '#entities';

const ceilingFormatter = new Intl.NumberFormat('en-EN', {
  style: 'unit',
  unit: 'foot',
});

export const formatMetarWind = (metar: AirMetar): string => {
  const { windDirDegrees, windSpeedKt, windGustKt } = metar;

  if (
    windSpeedKt === 0 ||
    (windDirDegrees === 0 && windSpeedKt === 0) ||
    windSpeedKt === null ||
    windSpeedKt === undefined
  ) {
    return 'Wind calm.';
  }

  let result: string;
  if (windDirDegrees === 'VRB') {
    result = `Variable ${windSpeedKt} knots`;
  } else {
    result = `From ${String(windDirDegrees).padStart(3, '0')}° ${windSpeedKt} knots`;
  }

  if (windGustKt) {
    result += ` gusting to ${windGustKt} knots`;
  }

  return `${result}.`;
};

export const formatMetarVisibility = (metar: AirMetar): string => {
  const vis = metar.visibilityStatuteMi;
  if (vis === null || vis === undefined || vis === '') {
    return '';
  }
  return `${vis} sm.`;
};

const SKY_COVER_LABELS: Record<string, string> = {
  SKC: 'no layers are present',
  CLR: 'no layers detected at or below 12,000 ft AGL',
  FEW: 'few',
  SCT: 'scattered layer',
  BKN: 'broken layer (ceiling)',
  OVC: 'overcast layer (ceiling)',
  VV: 'indefinite ceiling with a vertical visibility of',
};

export interface MetarCloudFormatted {
  skyCover: string;
  cloudBaseFtAgl: string;
  formatted: string;
}

export const formatMetarClouds = (metar: AirMetar): MetarCloudFormatted[] => {
  const { skyCondition, vertVisFt } = metar;
  if (!skyCondition?.length) {
    return [];
  }

  const clouds: MetarCloudFormatted[] = skyCondition
    .filter(c => c.skyCover)
    .map(({ skyCover, cloudBaseFtAgl }) => {
      const label = SKY_COVER_LABELS[skyCover] ?? skyCover;

      if (skyCover === 'SKC' || skyCover === 'CLR') {
        return { skyCover, cloudBaseFtAgl, formatted: label };
      }

      const height = Number.parseInt(cloudBaseFtAgl, 10);
      if (Number.isNaN(height)) {
        return { skyCover, cloudBaseFtAgl, formatted: label };
      }

      const heightFt = height;
      return {
        skyCover,
        cloudBaseFtAgl,
        formatted: `${label} at ${ceilingFormatter.format(heightFt)} AGL`,
      };
    });

  if (vertVisFt !== null && vertVisFt !== undefined && vertVisFt > 0) {
    clouds.push({
      skyCover: 'VV',
      cloudBaseFtAgl: String(vertVisFt),
      formatted: `indefinite ceiling with a vertical visibility of ${ceilingFormatter.format(vertVisFt)} AGL`,
    });
  }

  return clouds;
};

export const formatMetarWeather = (metar: AirMetar): string =>
  metar.wxString ?? '';

export const formatMetarTemperature = (metar: AirMetar): string => {
  const { tempC, dewpointC } = metar;
  if (tempC === null || tempC === undefined) {
    return '';
  }
  const parts = [`${tempC}°C`];
  if (dewpointC !== null && dewpointC !== undefined) {
    parts.push(`dewpoint ${dewpointC}°C`);
  }
  return `${parts.join(', ')}.`;
};

export const formatMetarAltimeter = (metar: AirMetar): string => {
  if (metar.altimInHg === null || metar.altimInHg === undefined) {
    return '';
  }
  return `${metar.altimInHg} inHg.`;
};

export const formatMetarSeaLevelPressure = (metar: AirMetar): string => {
  if (
    metar.seaLevelPressureMb === null ||
    metar.seaLevelPressureMb === undefined ||
    Number.isNaN(metar.seaLevelPressureMb)
  ) {
    return '';
  }
  return `${metar.seaLevelPressureMb} mb.`;
};

export const formatMetarPrecipitation = (metar: AirMetar): string => {
  const parts: string[] = [];
  if (metar.precipIn !== null && metar.precipIn !== undefined) {
    parts.push(`${metar.precipIn}" (since last METAR)`);
  }
  if (metar.pcp3hrIn && metar.pcp3hrIn !== '') {
    parts.push(`${metar.pcp3hrIn}" (3 hr)`);
  }
  if (metar.pcp6hrIn && metar.pcp6hrIn !== '') {
    parts.push(`${metar.pcp6hrIn}" (6 hr)`);
  }
  if (metar.pcp24hrIn && metar.pcp24hrIn !== '') {
    parts.push(`${metar.pcp24hrIn}" (24 hr)`);
  }
  if (metar.snowIn && metar.snowIn !== '') {
    parts.push(`snow ${metar.snowIn}"`);
  }
  return parts.join(', ');
};

export const formatMetarElevation = (metar: AirMetar): string => {
  if (metar.elevationM === null || metar.elevationM === undefined) {
    return '';
  }
  return `${metar.elevationM} m.`;
};

export const formatMetarObservationTime = (metar: AirMetar): string => {
  if (!metar.observationTime) {
    return '';
  }
  const d = new Date(metar.observationTime);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}Z`;
};
